import { Text, useToastContext } from "@ttc3k/trekker";
import { EnumWidgetType } from "gql/generated";
import { Code } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { WhereaboutsModal } from "shared/components";
import { Box, Flex } from "styled-system/jsx";

type EmbedWidgetModalProps = {
  isOpen: boolean;
  onClose: () => void;
  widgetId: string;
  widgetType: EnumWidgetType;
  accessId: string;
};

export const EmbedWidgetModal = ({
  isOpen,
  onClose,
  widgetId,
  widgetType,
  accessId,
}: EmbedWidgetModalProps) => {
  const { t } = useTranslation();
  const { toastFactory } = useToastContext();

  const handleCopyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      onClose();
      toastFactory.create({
        title: t("widgets:EMBED.COPIED") + "!",
        description: t("widgets:EMBED.SUCCESS_DESCRIPTION"),
      });
    } catch {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("widgets:EMBED.ERROR_DESCRIPTION"),
      });
    }
  };

  const scriptSrcOperator = `${import.meta.env.VITE_API_SERVER_URL}/embeddable/widget/operator/main.js`;
  const scriptSrcEvent = `${import.meta.env.VITE_API_SERVER_URL}/embeddable/widget/event/main.js`;

  const embedCodeString = {
    component:
      widgetType === EnumWidgetType.Operator
        ? `<whereabouts-operators-widget
  widget-id="${widgetId}"
  access-id="${accessId}"
></whereabouts-operators-widget>`
        : `<whereabouts-events-widget
  organization-id="${accessId}"
  widget-id="${widgetId}"
  style="display: block; min-height: 360px"
></whereabouts-events-widget>`,
    script: `<script
  src="${widgetType === EnumWidgetType.Operator ? scriptSrcOperator : scriptSrcEvent}"
  type="module"
></script>`,
  };
  return (
    <WhereaboutsModal
      isOpen={isOpen}
      onClose={onClose}
      HeaderIcon={Code}
      title={t("widgets:EMBED.TITLE")}
      saveButtonCopy={t("widgets:EMBED.COPY")}
      saveButtonProps={{
        onClick: () =>
          handleCopyToClipboard(
            embedCodeString.component + embedCodeString.script,
          ),
      }}
      cancelButtonProps={{ display: "none" }}
    >
      <Flex flexDir={"column"} gap={"150"}>
        <Text>{t("widgets:EMBED.DESCRIPTION")}</Text>
        <Box bg={"bg.wa.lightest"} p={"200"} borderRadius={"100"}>
          <Text
            color={"text.mid"}
            fontSize={"sm"}
            lineHeight={"sm"}
            fontFamily={"mono"}
            width={"full"}
            whiteSpace={"pre-wrap"}
            wordBreak={"break-all"}
          >
            {embedCodeString.component}
            <br />
            {embedCodeString.script}
          </Text>
        </Box>
      </Flex>
    </WhereaboutsModal>
  );
};
